import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { X, ChevronLeft, ChevronRight } from "lucide-react";
import PageHero from "@/components/PageHero";
import CTASection from "@/components/CTASection";
import { Reveal } from "@/components/Reveal";
import { GALLERY, IMG } from "@/data/site";

export default function OurWork() {
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    if (selected === null) return;
    const onKey = (e) => {
      if (e.key === "Escape") setSelected(null);
      if (e.key === "ArrowRight") setSelected((s) => (s + 1) % GALLERY.length);
      if (e.key === "ArrowLeft") setSelected((s) => (s - 1 + GALLERY.length) % GALLERY.length);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [selected]);

  return (
    <>
      <PageHero
        eyebrow="Portfolio"
        title="Examples of our welding work"
        lead="Every photo below shows actual projects completed by Park and Spark Mobile Welding, LLC — fabrication, repairs, and installations across Montana and northern Wyoming."
      />

      <section className="py-16 sm:py-20">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="columns-1 sm:columns-2 lg:columns-3 gap-4 [column-fill:_balance]">
            {GALLERY.map((g, i) => (
              <Reveal key={g.src} delay={(i % 3) * 0.05} className="mb-4 break-inside-avoid">
                <button
                  type="button"
                  onClick={() => setSelected(i)}
                  data-testid={`gallery-item-${i}`}
                  aria-label={`View photo: ${g.alt}`}
                  className="group relative block w-full overflow-hidden border border-line hover:border-gold/60 transition-colors"
                >
                  <img
                    src={IMG(g.src)}
                    alt={g.alt}
                    loading="lazy"
                    className="w-full object-cover group-hover:scale-[1.03] transition-transform duration-500"
                  />
                  <span
                    className="absolute inset-0 bg-ink/0 group-hover:bg-ink/20 transition-colors"
                    aria-hidden="true"
                  />
                </button>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {selected !== null && (
        <div
          className="fixed inset-0 z-[80] bg-ink/95 backdrop-blur-sm flex items-center justify-center p-4"
          data-testid="lightbox"
          onClick={() => setSelected(null)}
        >
          <button
            type="button"
            onClick={() => setSelected(null)}
            data-testid="lightbox-close-button"
            aria-label="Close photo"
            className="absolute top-5 right-5 inline-flex items-center justify-center w-11 h-11 border border-line bg-coal text-slate-200 hover:border-gold hover:text-gold transition-colors"
          >
            <X size={20} />
          </button>
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              setSelected((s) => (s - 1 + GALLERY.length) % GALLERY.length);
            }}
            data-testid="lightbox-prev-button"
            aria-label="Previous photo"
            className="absolute left-4 sm:left-8 inline-flex items-center justify-center w-11 h-11 border border-line bg-coal text-slate-200 hover:border-gold hover:text-gold transition-colors"
          >
            <ChevronLeft size={20} />
          </button>
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              setSelected((s) => (s + 1) % GALLERY.length);
            }}
            data-testid="lightbox-next-button"
            aria-label="Next photo"
            className="absolute right-4 sm:right-8 inline-flex items-center justify-center w-11 h-11 border border-line bg-coal text-slate-200 hover:border-gold hover:text-gold transition-colors"
          >
            <ChevronRight size={20} />
          </button>
          <figure className="max-w-5xl w-full" onClick={(e) => e.stopPropagation()}>
            <img
              src={IMG(GALLERY[selected].src)}
              alt={GALLERY[selected].alt}
              data-testid="lightbox-image"
              className="w-full max-h-[82vh] object-contain border border-line bg-coal"
            />
            <figcaption className="mt-3 text-center text-sm text-slate-400">
              {GALLERY[selected].alt}
            </figcaption>
          </figure>
        </div>
      )}

      <CTASection />
    </>
  );
}
